import React, { createContext, useState, useContext, useEffect } from 'react';
import { productAPI, sponsorAPI, contentAPI } from '../services/api';

const ContentContext = createContext({});

export const useContent = () => useContext(ContentContext);

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({
    contact: {
      address: '',
      phone: '',
      email: '',
      hours: {
        weekday: '',
        saturday: '',
        sunday: ''
      }
    },
    homepage: {
      title: '',
      description: ''
    },
    products: [],
    sponsors: []
  });
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [features, setFeatures] = useState([]);
  const [homeImages, setHomeImages] = useState([]);
  const [aboutImages, setAboutImages] = useState([]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const result = await contentAPI.getContent();
      if (result.success && result.data) {
        const data = result.data;
        setContent(prev => ({
          ...prev,
          ...data,
          products: Array.isArray(data.products) ? data.products : [],
          sponsors: Array.isArray(data.sponsors) ? data.sponsors : []
        }));
      }
      return result;
    } catch (error) {
   
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getContentBySection = async (section) => {
    try {
      return await contentAPI.getContentBySection(section);
    } catch (error) {
   
      return { success: false, error: error.message };
    }
  };

  const fetchProducts = async (filters = {}) => {
    setLoading(true);
    try {
      const result = await productAPI.getProducts(filters);
   
      if (result.success && result.data) {
        const products = Array.isArray(result.data) ? result.data : (result.data.products || []);
    
        setContent(prev => ({
          ...prev,
          products: products
        }));
      }
      return result;
    } catch (error) {
   
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateContent = async (section, data) => {
    try {
      const result = await contentAPI.updateContent(section, data);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          [section]: data
        }));
      }
      return result;
    } catch (error) {
  
      return { success: false, error: error.message };
    }
  };

  const addProduct = async (productData) => {
    try {
      const result = await productAPI.createProduct(productData);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          products: Array.isArray(prev.products) ? [...prev.products, result.data] : [result.data]
        }));
      }
      return result;
    } catch (error) {
      
      return { success: false, error: error.message };
    }
  };

  const updateProduct = async (id, productData) => {
    try {
      const result = await productAPI.updateProduct(id, productData);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          products: Array.isArray(prev.products) 
            ? prev.products.map(p => p.id === id ? result.data : p)
            : [result.data]
        }));
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteProduct = async (id) => {
    try {
      const result = await productAPI.deleteProduct(id);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          products: Array.isArray(prev.products)
            ? prev.products.filter(p => p.id !== id)
            : []
        }));
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const addSponsor = async (sponsorData) => {
    try {
      const result = await sponsorAPI.createSponsor(sponsorData);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          sponsors: Array.isArray(prev.sponsors) ? [...prev.sponsors, result.data] : [result.data]
        }));
      }
      return result;
    } catch (error) {
  
      return { success: false, error: error.message };
    }
  };

  const updateSponsor = async (id, sponsorData) => {
    try {
      const result = await sponsorAPI.updateSponsor(id, sponsorData);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          sponsors: Array.isArray(prev.sponsors)
            ? prev.sponsors.map(s => s.id === id ? result.data : s)
            : [result.data]
        }));
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteSponsor = async (id) => {
    try {
      const result = await sponsorAPI.deleteSponsor(id);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          sponsors: Array.isArray(prev.sponsors)
            ? prev.sponsors.filter(s => s.id !== id)
            : []
        }));
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateContact = async (contactData) => {
    try {
      const result = await contentAPI.updateContent('contact', contactData);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          contact: contactData
        }));
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateHomepage = async (homepageData) => {
    try {
      const result = await contentAPI.updateContent('homepage', homepageData);
      if (result.success) {
        setContent(prev => ({
          ...prev,
          homepage: homepageData
        }));
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const fetchServices = async () => {
  try {
    const result = await contentAPI.getContentBySection('services');
    if (result.success && result.data) {
      setServices(result.data.services || []);
    }
    return result;
  } catch (error) {
    console.error('Failed to fetch services:', error);
    return { success: false, error: error.message };
  }
};

const fetchFeatures = async () => {
  try {
    const result = await contentAPI.getContentBySection('features');
    if (result.success && result.data) {
      setFeatures(result.data.features || []);
    }
    return result;
  } catch (error) {
    console.error('Failed to fetch features:', error);
    return { success: false, error: error.message };
  }
};

const fetchHomeImages = async () => {
  try {
    const result = await contentAPI.getContentBySection('home_images');
    if (result.success && result.data) {
      setHomeImages(result.data.images || []);
    }
    return result;
  } catch (error) {
    console.error('Failed to fetch home images:', error);
    return { success: false, error: error.message };
  }
};

const fetchAboutImages = async () => {
  try {
    const result = await contentAPI.getContentBySection('about_images');
    if (result.success && result.data) {
      setAboutImages(result.data || []);
    }
    return result;
  } catch (error) {
    console.error('Failed to fetch about images:', error);
    return { success: false, error: error.message };
  }
};

const updateServices = async (servicesData) => {
  try {
    const result = await contentAPI.updateContent('services', { services: servicesData });
    if (result.success) {
      setServices(servicesData);
    }
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const updateFeatures = async (featuresData) => {
  try {
    const result = await contentAPI.updateContent('features', { features: featuresData });
    if (result.success) {
      setFeatures(featuresData);
    }
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const updateHomeImages = async (imagesData) => {
  try {
    const result = await contentAPI.updateContent('home_images', { images: imagesData });
    if (result.success) {
      setHomeImages(imagesData);
    }
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const updateAboutImages = async (imagesData) => {
  try {
    const result = await contentAPI.updateContent('about_images', imagesData );
    if (result.success) {
      setAboutImages(imagesData);
    }
    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

  useEffect(() => {
    fetchContent();
    fetchServices();
    fetchFeatures();
    fetchHomeImages();
    fetchAboutImages();
  }, []);

  const value = {
    content,
    loading,
    fetchContent,
    fetchProducts,
    getContentBySection,
    updateContent,
    addProduct,
    updateProduct,
    deleteProduct,
    addSponsor,
    updateSponsor,
    deleteSponsor,
    updateContact,
    updateHomepage,
    services,
    features,
    homeImages,
    aboutImages,
    fetchServices,
    fetchFeatures,
    fetchHomeImages,
    fetchAboutImages,
    updateServices,
    updateFeatures,
    updateHomeImages,
    updateAboutImages,

    products: content.products,
    sponsors: content.sponsors
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};